import { MoreHorizontal, Settings2, Trash2 } from "lucide-react";
import { Button } from "~/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "~/components/ui/dropdown-menu";
import { TableBody, TableCell, TableHead, TableHeader, TableRow } from "~/components/ui/table";
import { Tooltip, TooltipContent, TooltipTrigger } from "~/components/ui/tooltip";
import { type PartialEvalCase } from "~/services/evals";
import { CaseNameCell, EvalTable, rowActivateProps, SortableHead } from "./EvalTable";
import type { TableSort } from "./TableSort";
import { CaseSortKey } from "./constants/sorting";
import { DatasetNames, type DatasetRefs, TagChips } from "./primitives/chips";
import { formatDuration, formatEvalTimestamp } from "./primitives/format";
import type { CaseHealthRow } from "./selectors/cases";
import { parseApiTimestamp } from "./selectors/core";

interface CaseConfigTableProps {
  /** Already sorted; the header only reports and sets the order. */
  rows: CaseHealthRow[];
  sort: TableSort<CaseSortKey>;
  /** Catalog datasets by id, for each row's dataset names and colours. */
  datasets: DatasetRefs;
  onSelectCase: (caseId: string) => void;
  onDeleteCase: (evalCase: PartialEvalCase) => void;
}

/**
 * The configuration column set: what each case tests, and how it is scored.
 *
 * The same rows as the health grid, in the same order and at the same heights, so
 * a reviewer who found a failing case and switched columns is still looking at it.
 * No score column — what a case is configured to do holds still, how it did last
 * week does not, and a column of it made this a partial copy of the grid.
 */
export const CaseConfigTable = ({
  rows,
  sort,
  datasets,
  onSelectCase,
  onDeleteCase,
}: CaseConfigTableProps) => (
  <div className="overflow-x-auto">
    <EvalTable>
      <TableHeader>
        <TableRow>
          <SortableHead columnKey={CaseSortKey.NAME} label="Case" sort={sort} />
          <SortableHead columnKey={CaseSortKey.SUITE} label="Tags" sort={sort} />
          <SortableHead columnKey={CaseSortKey.DATASETS} label="Dataset" sort={sort} />
          <SortableHead columnKey={CaseSortKey.THRESHOLD} label="Pass at" alignRight sort={sort} />
          <SortableHead
            columnKey={CaseSortKey.LATENCY}
            label="Latency limit"
            alignRight
            sort={sort}
          />
          <TableHead>Author</TableHead>
          <SortableHead columnKey={CaseSortKey.UPDATED} label="Edited" sort={sort} />
          <TableHead className="w-8" />
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((row) => (
          <TableRow
            key={row.evalCase.id}
            {...rowActivateProps(() => onSelectCase(row.evalCase.id), "group")}
          >
            <TableCell className="max-w-[320px]">
              <CaseNameCell
                name={row.evalCase.name}
                description={row.evalCase.description}
                onSelect={() => onSelectCase(row.evalCase.id)}
              />
            </TableCell>
            <TableCell className="max-w-[260px]">
              <TagChips tags={row.evalCase.tags} max={2} />
            </TableCell>
            <TableCell className="max-w-[260px]">
              <DatasetNames
                ids={row.evalCase.dataset_ids}
                datasets={datasets}
                className="text-xs"
                max={1}
              />
            </TableCell>
            <TableCell className="text-right text-xs tabular-nums">
              {row.evalCase.pass_threshold ?? (
                <Tooltip delayDuration={200}>
                  <TooltipTrigger className="cursor-help text-muted-foreground">
                    Assertions
                  </TooltipTrigger>
                  <TooltipContent side="top" className="max-w-xs">
                    <p className="text-xs">
                      No score threshold. Pass or fail comes from the assertions alone — the
                      tool-call counts and table checks.
                    </p>
                  </TooltipContent>
                </Tooltip>
              )}
            </TableCell>
            <TableCell className="text-right text-xs tabular-nums">
              {row.evalCase.max_latency_seconds == null ? (
                <span className="text-muted-foreground">None</span>
              ) : (
                <Tooltip delayDuration={200}>
                  <TooltipTrigger className="cursor-help text-foreground">
                    {formatDuration(row.evalCase.max_latency_seconds)}
                  </TooltipTrigger>
                  <TooltipContent side="top" className="max-w-xs">
                    <p className="text-xs">
                      Not advisory. A latency limit adds an assertion, so a run slower than this
                      fails the case however well it answered.
                    </p>
                  </TooltipContent>
                </Tooltip>
              )}
            </TableCell>
            <TableCell className="max-w-[160px] text-xs text-muted-foreground">
              {/* A real column on the case, and a run's `case_created_by` can
                  select on it — so who wrote a case is part of what a run
                  covers, not trivia. */}
              <span className="block truncate" title={row.evalCase.created_by ?? undefined}>
                {row.evalCase.created_by ?? "Imported"}
              </span>
            </TableCell>
            <TableCell className="text-xs whitespace-nowrap text-muted-foreground">
              {formatEvalTimestamp(parseApiTimestamp(row.evalCase.updated_at))}
            </TableCell>
            <TableCell>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="size-7 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100 focus-visible:opacity-100 data-[state=open]:opacity-100"
                    aria-label={`Actions for ${row.evalCase.name}`}
                  >
                    <MoreHorizontal className="size-4" aria-hidden />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => onSelectCase(row.evalCase.id)}>
                    <Settings2 className="size-4" aria-hidden />
                    Review configuration
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    variant="destructive"
                    onClick={() => onDeleteCase(row.evalCase)}
                  >
                    <Trash2 className="size-4" aria-hidden />
                    Delete case
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </EvalTable>
  </div>
);
